import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BillingApi,
  type BillingSeatChangePreview,
  BillingSeatChangePreviewDirectionEnum,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { PlatformI18nService } from '@tourfold/shared-kernels/i18n';

@Component({
  selector: 'tf-update-seats-dialog',
  imports: [CommonModule, TranslateModule, DialogModule, ButtonModule],
  templateUrl: './update-seats-dialog.html',
  styleUrl: './update-seats-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateSeatsDialog {
  readonly open = input.required<boolean>();
  readonly currentSeats = input.required<number>();
  readonly activeUsers = input.required<number>();
  readonly pricePerSeatMinor = input<number | null>(null);
  readonly currency = input<string>('eur');
  readonly isConfirming = input<boolean>(false);

  readonly closed = output<void>();
  readonly confirmed = output<number>();

  private readonly billingFrontendApi = inject(BillingApi);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformI18n = inject(PlatformI18nService);

  protected readonly directions = BillingSeatChangePreviewDirectionEnum;

  protected readonly seatsDraft = signal<number>(0);
  protected readonly preview = signal<BillingSeatChangePreview | null>(null);
  protected readonly isPreviewing = signal(false);
  protected readonly previewFailed = signal(false);
  protected readonly consentAccepted = signal(false);

  protected readonly direction = computed(() => this.preview()?.direction ?? null);
  protected readonly delta = computed(() => this.seatsDraft() - this.currentSeats());
  protected readonly seatsMin = computed(() => Math.max(1, this.activeUsers()));

  protected readonly proration = computed(() => {
    const preview = this.preview();
    if (!preview?.periodStart || !preview?.periodEnd) {
      return null;
    }
    const start = preview.periodStart.getTime();
    const end = preview.periodEnd.getTime();
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.max(1, Math.round((end - start) / oneDay));
    const daysRemaining = Math.max(0, Math.min(totalDays, Math.round((end - now) / oneDay)));
    return {
      totalDays,
      daysRemaining,
      proration: totalDays === 0 ? 0 : daysRemaining / totalDays,
    };
  });

  protected readonly canConfirm = computed(() => {
    if (this.isConfirming() || this.isPreviewing()) {
      return false;
    }
    const preview = this.preview();
    if (!preview) {
      return false;
    }
    if (preview.direction === this.directions.Same) {
      return false;
    }
    return this.consentAccepted();
  });

  protected readonly confirmButtonLabel = computed<string>(() => {
    const preview = this.preview();
    if (!preview) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.SEATS.CONFIRM_BUTTON');
    }
    if (preview.direction === this.directions.Increase) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.SEATS.CONFIRM_AND_PAY', {
        amount: this.formatAmount(preview.immediateAmountMinor, preview.currency),
      });
    }
    if (preview.direction === this.directions.Decrease) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.SEATS.SCHEDULE_BUTTON', {
        date: this.formatDate(preview.effectiveAt ?? preview.periodEnd),
      });
    }
    return this.translate.instant('SETTINGS_BILLING.PLAN.SEATS.CONFIRM_BUTTON');
  });

  /**
   * Tracks the previous value of `open()` so we only reset the dialog's local state on the
   * rising edge (closed → open). Without this, the effect would re-run any time
   * `seatsMin`/`currentSeats` updated (e.g. via an SSE billing refresh) and wipe the user's
   * in-progress seat adjustments mid-edit.
   */
  private wasOpen = false;

  constructor() {
    effect(() => {
      const isOpen = this.open();
      if (isOpen && !this.wasOpen) {
        const initialSeats = untracked(() => Math.max(this.seatsMin(), this.currentSeats()));
        this.seatsDraft.set(initialSeats);
        this.consentAccepted.set(false);
        this.preview.set(null);
        this.previewFailed.set(false);
        // Only fire a preview if the dialog opens at a quantity that actually differs
        // from what's currently active — Confirm is disabled for "no change" anyway,
        // and a failing preview-on-open would otherwise block the dialog with a generic
        // error even though the user hasn't done anything yet.
        const initialDelta = untracked(() => initialSeats - this.currentSeats());
        if (initialDelta !== 0) {
          this.requestPreview();
        }
      }
      this.wasOpen = isOpen;
    });
  }

  protected onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closed.emit();
    }
  }

  protected adjustSeats(delta: number): void {
    const next = Math.max(this.seatsMin(), this.seatsDraft() + delta);
    if (next === this.seatsDraft()) {
      return;
    }
    this.seatsDraft.set(next);
    this.consentAccepted.set(false);
    this.requestPreview();
  }

  protected onSeatsInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const parsed = Number(target?.value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const next = Math.max(this.seatsMin(), Math.min(9999, Math.trunc(parsed)));
    this.seatsDraft.set(next);
    this.consentAccepted.set(false);
  }

  protected onSeatsInputCommit(): void {
    this.requestPreview();
  }

  protected onConsentChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.consentAccepted.set(target?.checked === true);
  }

  protected onConfirm(): void {
    if (!this.canConfirm()) {
      return;
    }
    this.confirmed.emit(this.seatsDraft());
  }

  protected onCancel(): void {
    this.closed.emit();
  }

  protected formatAmount(amount: number | null | undefined, currency: string): string {
    if (amount == null) {
      return '—';
    }
    const normalizedCurrency = (currency ?? this.currency() ?? 'EUR').toUpperCase();
    try {
      return new Intl.NumberFormat(this.platformI18n.resolveFormatLocale(), {
        style: 'currency',
        currency: normalizedCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    } catch {
      return `${(amount / 100).toFixed(2)} ${normalizedCurrency}`;
    }
  }

  protected formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '—';
    }
    return new Intl.DateTimeFormat(this.platformI18n.resolveFormatLocale(), {
      dateStyle: 'medium',
    }).format(new Date(date));
  }

  protected formatPercent(value: number): string {
    return new Intl.NumberFormat(this.platformI18n.resolveFormatLocale(), {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  }

  protected deltaLabel(delta: number): string {
    if (delta === 0) {
      return this.translate.instant('SETTINGS_BILLING.PLAN.SEATS.DELTA_NONE');
    }
    if (delta > 0) {
      const key =
        delta === 1
          ? 'SETTINGS_BILLING.PLAN.SEATS.DELTA_ADD_ONE'
          : 'SETTINGS_BILLING.PLAN.SEATS.DELTA_ADD_MANY';
      return this.translate.instant(key, { count: delta });
    }
    const abs = Math.abs(delta);
    const key =
      abs === 1
        ? 'SETTINGS_BILLING.PLAN.SEATS.DELTA_REMOVE_ONE'
        : 'SETTINGS_BILLING.PLAN.SEATS.DELTA_REMOVE_MANY';
    return this.translate.instant(key, { count: delta });
  }

  protected minimumHint(active: number): string {
    const key =
      active === 1
        ? 'SETTINGS_BILLING.PLAN.SEATS.MINIMUM_HINT_ONE'
        : 'SETTINGS_BILLING.PLAN.SEATS.MINIMUM_HINT_MANY';
    return this.translate.instant(key, { count: active });
  }

  private requestPreview(): void {
    const quantity = this.seatsDraft();
    if (quantity == null || quantity < this.seatsMin()) {
      return;
    }
    this.isPreviewing.set(true);
    this.previewFailed.set(false);
    this.preview.set(null);

    from(
      this.billingFrontendApi.previewBillingSeats({
        previewBillingSeatsRequest: { quantity },
      })
    )
      .pipe(
        finalize(() => this.isPreviewing.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (preview) => this.preview.set(preview),
        error: () => this.previewFailed.set(true),
      });
  }
}
